import InspectionStatus from '@/enums/InspectionStatus.enum';
import type { Inspection } from '@/types/Inspection.type';

type InspectionStartIdentity = Pick<Inspection, 'id' | 'inspectionStatusId' | 'inspectedBy'>;

/** Translation keys when inspection is assigned to another user, by status. */
const BLOCK_REASON_BY_STATUS_OTHER_INSPECTOR: Partial<Record<string, string>> = {
  [InspectionStatus.STARTED]: 'inspection_already_started_by_other_inspector_error',
  [InspectionStatus.FINISHED]: 'inspection_already_finished_by_other_inspector_error',
  [InspectionStatus.CLOSED]: 'inspection_already_closed_by_other_inspector_error',
  [InspectionStatus.PAUSED]: 'inspection_paused_by_other_inspector_error',
  [InspectionStatus.PLANNED]: 'inspection_assigned_to_other_inspector_error',
  [InspectionStatus.CREATED]: 'inspection_not_planned_error',
};

export function isInspectionStatus(
  statusId: string | undefined,
  expectedStatusId: InspectionStatus
): boolean {
  return statusId?.toLowerCase() === expectedStatusId.toLowerCase();
}

function isAssignedToOtherUser(
  inspectedBy: string | undefined,
  currentUserId: string | undefined
): boolean {
  if (!inspectedBy || !currentUserId) {
    return false;
  }
  return inspectedBy.toLowerCase() !== currentUserId.toLowerCase();
}

/**
 * Returns true when the inspection is STARTED and assigned to the current user.
 * Used to skip the start-workflow guard after page reload when navigation state is lost.
 */
export function isInspectionStartedByCurrentUser(
  inspection: InspectionStartIdentity | undefined | null,
  inspectionId: string | undefined,
  currentUserId: string | undefined
): boolean {
  if (!inspectionId || !currentUserId || !inspection) {
    return false;
  }
  if (inspection.id !== inspectionId) {
    return false;
  }
  return (
    isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.STARTED) &&
    inspection.inspectedBy?.toLowerCase() === currentUserId.toLowerCase()
  );
}

/**
 * Returns true when the inspection is STARTED or FINISHED and assigned to the current user.
 * Used to auto-open details without the confirm-start prompt.
 */
export function isInspectionResumableByCurrentUser(
  inspection: InspectionStartIdentity | undefined | null,
  inspectionId: string | undefined,
  currentUserId: string | undefined
): boolean {
  if (!inspectionId || !currentUserId || !inspection) {
    return false;
  }
  if (inspection.id !== inspectionId) {
    return false;
  }
  const isResumableStatus =
    isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.STARTED) ||
    isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.FINISHED);
  return isResumableStatus && inspection.inspectedBy?.toLowerCase() === currentUserId.toLowerCase();
}

/**
 * Returns a translation key if the current user is not allowed to start this inspection
 * when start-inspection-check is enabled; otherwise null.
 */
export function getStartBlockReason(
  inspection: Partial<Inspection>,
  currentUserId: string | undefined
): string | null {
  const { inspectionStatusId, inspectedBy } = inspection;
  if (isAssignedToOtherUser(inspectedBy, currentUserId) && inspectionStatusId) {
    const normalizedStatusId = Object.values(InspectionStatus).find((statusId) =>
      isInspectionStatus(inspectionStatusId, statusId)
    );
    const blockReason = normalizedStatusId
      ? BLOCK_REASON_BY_STATUS_OTHER_INSPECTOR[normalizedStatusId]
      : null;
    if (blockReason) {
      return blockReason;
    }
  }
  if (isInspectionStatus(inspectionStatusId, InspectionStatus.CREATED)) {
    return 'inspection_not_planned_error';
  }
  return null;
}

export type InspectionStartPageDecision =
  | { action: 'open' }
  | { action: 'block'; messageKey: string }
  | { action: 'confirm' };

export type GetInspectionStartPageDecisionParams = {
  inspection: Partial<Inspection>;
  inspectionId: string | undefined;
  currentUserId: string | undefined;
  startInspectionCheck: boolean;
};

/**
 * Pure entry decision for the start page after inspection details are hydrated.
 * CLOSED is always blocked regardless of tenant config.
 */
export function getInspectionStartPageDecision(
  params: GetInspectionStartPageDecisionParams
): InspectionStartPageDecision {
  const { inspection, inspectionId, currentUserId, startInspectionCheck } = params;
  const { inspectionStatusId, inspectedBy } = inspection;
  if (isInspectionStatus(inspectionStatusId, InspectionStatus.CLOSED)) {
    return {
      action: 'block',
      messageKey: 'inspection_already_closed_by_other_inspector_error',
    };
  }
  if (
    isInspectionResumableByCurrentUser(
      {
        id: inspection.id ?? inspectionId ?? '',
        inspectionStatusId,
        inspectedBy,
      },
      inspectionId,
      currentUserId
    )
  ) {
    return { action: 'open' };
  }
  if (isInspectionStatus(inspectionStatusId, InspectionStatus.FINISHED) && !inspectedBy) {
    return {
      action: 'block',
      messageKey: 'inspection_already_finished_by_other_inspector_error',
    };
  }
  if (startInspectionCheck) {
    const blockReason = getStartBlockReason(inspection, currentUserId);
    if (blockReason) {
      return { action: 'block', messageKey: blockReason };
    }
  }
  return { action: 'confirm' };
}

export type ShouldSkipInspectionStartWorkflowParams = {
  /** Ephemeral React Router flag set when navigating from the start page. */
  skipStartWorkFlowFromNavigation: boolean;
  inspectionId: string | undefined;
  currentUserId: string | undefined;
  /** Active working copy (redux-persist → IndexedDB). */
  openedInspection: InspectionStartIdentity | undefined | null;
  /** List cache row for the same inspection (redux-persist → IndexedDB). */
  cachedInspection: InspectionStartIdentity | undefined | null;
  /** True when the user has unsaved edits on the opened inspection. */
  isOpenedInspectionModified: boolean;
};

/**
 * Determines whether the inspection details page should bypass the start-workflow redirect.
 *
 * Durable checks use existing persisted Redux slices only — no new storage.
 * `openedInspection` is the primary source (active session). `inspectionsData` is consulted
 * as a fallback because `gotoInspection` upserts STARTED status there before
 * `openInspectionById` completes.
 */
export function shouldSkipInspectionStartWorkflow(
  params: ShouldSkipInspectionStartWorkflowParams
): boolean {
  const {
    skipStartWorkFlowFromNavigation,
    inspectionId,
    currentUserId,
    openedInspection,
    cachedInspection,
    isOpenedInspectionModified,
  } = params;
  if (skipStartWorkFlowFromNavigation) {
    return true;
  }
  if (isInspectionResumableByCurrentUser(openedInspection, inspectionId, currentUserId)) {
    return true;
  }
  if (isInspectionResumableByCurrentUser(cachedInspection, inspectionId, currentUserId)) {
    return true;
  }
  if (inspectionId && openedInspection?.id === inspectionId && isOpenedInspectionModified) {
    return true;
  }
  return false;
}
